import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import PillarPageLayout from "./PillarPageLayout";
import AuthorByline from "@/components/AuthorByline";

const FAQ = [
  {
    q: "¿Qué es Immunocal y para qué sirve?",
    a: "Immunocal es una proteína aislada de suero bovino patentada por Immunotec. Es el precursor natural más eficiente de glutatión (GSH) — el antioxidante maestro del cuerpo. Apoya el sistema inmunológico, la energía celular y la defensa frente al estrés oxidativo.",
  },
  {
    q: "¿Cuál es la diferencia entre Immunocal, Immunocal Platinum e Immunocal Sport?",
    a: "Immunocal es la fórmula original (proteína aislada de suero). Immunocal Platinum añade citrato de potasio y magnesio para regulación del pH y soporte óseo — ideal para adultos +40. Immunocal Sport agrega electrolitos y creatina, formulado para atletas y entrenamiento intenso.",
  },
  {
    q: "¿Cuánto cuesta Immunocal en México?",
    a: "El precio público de Immunotec varía por presentación. Como Distribuidor Independiente, Eliora Longevity te puede dar acceso a Immunocal con hasta 45% de descuento mediante el Kit de Inicio Immunotec, además de una asesoría personalizada gratuita.",
  },
  {
    q: "¿Cómo se toma Immunocal?",
    a: "La dosis recomendada típica es 1 sobre (10g) al día disuelto en agua o jugo frío (nunca caliente — el calor desnaturaliza la proteína). Para mantenimiento: 1 sobre/día; para apoyo intensivo: 2-3 sobres/día. Siempre con el estómago vacío y al menos 20 minutos antes de comer.",
  },
  {
    q: "¿Immunocal tiene efectos secundarios?",
    a: "Immunocal es un suplemento natural generalmente seguro. Personas con alergia severa a la proteína de leche/lácteos deben consultar a su médico antes de consumirlo. No se recomienda en menores de 1 año salvo bajo supervisión profesional.",
  },
  {
    q: "¿Immunocal cura el cáncer u otras enfermedades?",
    a: "NO. Immunocal es un suplemento alimenticio, no un medicamento. Apoya el sistema inmunológico y la defensa antioxidante elevando los niveles de glutatión, pero no diagnostica, trata, cura ni previene ninguna enfermedad. Siempre consulta a tu médico.",
  },
];

const RELATED = [
  { label: "Glutatión: el antioxidante maestro", href: "/glutation", desc: "Por qué los niveles de GSH son críticos para la salud celular y cómo aumentarlos." },
  { label: "Cómo fortalecer tu sistema inmunológico", href: "/sistema-inmunologico", desc: "Guía completa basada en medicina funcional para una inmunidad fuerte." },
  { label: "Blog Eliora", href: "/blog", desc: "Artículos sobre biohacking, longevidad y medicina funcional." },
];

export default function ImmunocalPage(props) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Immunocal México: Qué es, beneficios e ingredientes [Guía 2026]</title>
        <meta name="description" content="Immunocal en México: qué es, cómo funciona, beneficios para el sistema inmunológico, ingredientes y guía de compra con descuento. Distribuidor Independiente Immunotec." />
        <link rel="canonical" href="https://elioralongevity.com/immunocal" />
        <meta property="og:title" content="Immunocal México: Qué es, beneficios e ingredientes [Guía 2026]" />
        <meta property="og:description" content="Guía completa de Immunocal — precursor natural de glutatión. Beneficios, dosis, presentaciones y dónde comprar con descuento en México." />
        <meta property="og:url" content="https://elioralongevity.com/immunocal" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        })}</script>
      </Helmet>

      <PillarPageLayout
        {...props}
        meta={{
          eyebrow: "Guía completa · 2026",
          h1: "Immunocal: Qué es, beneficios e ingredientes",
          lead: "Todo lo que necesitas saber sobre Immunocal, el precursor de glutatión patentado por Immunotec — desde su ciencia hasta cómo tomarlo y dónde conseguirlo con descuento en México.",
        }}
        breadcrumbs={[{ label: "Inicio", href: "/" }, { label: "Immunocal" }]}
        faq={FAQ}
        relatedLinks={RELATED}
      >
        <AuthorByline date="2026-05-30" minutes={8} />

        <h2 className="font-serif text-3xl mt-2 mb-4">¿Qué es Immunocal?</h2>
        <p className="text-[#1a2421] leading-relaxed mb-5">
          <strong>Immunocal</strong> es una proteína aislada de suero bovino <em>bioactiva</em>, patentada por la compañía canadiense Immunotec. Su característica única: aporta los <strong>tres aminoácidos azufrados</strong> (cisteína, glutamina y glicina) en la conformación necesaria para que el cuerpo produzca su propio <strong>glutatión</strong> intracelular, considerado el <em>antioxidante maestro</em>.
        </p>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          A diferencia de los suplementos de glutatión oral (que se degradan en el estómago), Immunocal funciona como un <strong>precursor</strong>: entrega los ladrillos para que cada célula produzca su propio glutatión, de forma sostenida y eficiente.
        </p>

        <h2 className="font-serif text-3xl mt-10 mb-4">¿Para qué sirve Immunocal?</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">
          Immunocal apoya principalmente tres áreas, todas vinculadas con la producción intracelular de glutatión:
        </p>
        <ul className="list-disc pl-6 space-y-3 mb-8 text-[#1a2421]">
          <li><strong>Sistema inmunológico:</strong> el glutatión es esencial para el funcionamiento de los linfocitos T y la respuesta inmune adaptativa. Niveles bajos de GSH se asocian con mayor susceptibilidad a infecciones.</li>
          <li><strong>Defensa antioxidante:</strong> neutraliza radicales libres derivados del estrés, contaminación, ejercicio intenso y envejecimiento, reduciendo el daño celular acumulado.</li>
          <li><strong>Energía y recuperación:</strong> al optimizar la función mitocondrial, contribuye a una sensación de mayor vitalidad y mejor recuperación post-esfuerzo.</li>
        </ul>

        <h2 className="font-serif text-3xl mt-10 mb-4">Ingredientes y composición</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">
          La fórmula <strong>Immunocal Original</strong> contiene únicamente:
        </p>
        <ul className="list-disc pl-6 space-y-2 mb-8 text-[#1a2421]">
          <li>Proteína aislada de suero bovino no desnaturalizada (PURE WHEY PROTEIN ISOLATE™)</li>
          <li>Lactoferrina, alfa-lactoalbúmina, beta-lactoglobulina, seroalbúmina bovina (BSA)</li>
          <li>Cero azúcar añadida, cero grasas trans, libre de gluten</li>
        </ul>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          Está respaldado por más de <strong>30 años de investigación</strong> en universidades como McGill (Canadá) y publicaciones revisadas por pares en revistas como <em>Anticancer Research</em>, <em>Antioxidants & Redox Signaling</em>, y <em>Journal of Nutrition</em>.
        </p>

        <div className="not-prose bg-[#f9f6f0] border border-[#2c4a3b]/10 rounded-2xl p-5 my-6 text-sm">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold mb-2">Referencias científicas</p>
          <ul className="space-y-2 text-[#5c6b61]">
            <li>• Bounous G, Gold P. "The biological activity of undenatured dietary whey proteins: role of glutathione." <em>Clin Invest Med</em>. 1991. <a href="https://pubmed.ncbi.nlm.nih.gov/1782728/" target="_blank" rel="noopener noreferrer" className="text-[#bc6c25] hover:underline">PubMed</a></li>
            <li>• Lothian B, Grey V, Lands LC. "Effect of whey protein to modulate immune response in children with atopic asthma." <em>Int J Food Sci Nutr</em>. 2006. <a href="https://pubmed.ncbi.nlm.nih.gov/17162310/" target="_blank" rel="noopener noreferrer" className="text-[#bc6c25] hover:underline">PubMed</a></li>
            <li>• Pizzorno J. "Glutathione!" <em>Integr Med (Encinitas)</em>. 2014. <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4684116/" target="_blank" rel="noopener noreferrer" className="text-[#bc6c25] hover:underline">NIH PMC</a></li>
          </ul>
        </div>

        <h2 className="font-serif text-3xl mt-10 mb-4">Presentaciones: Original, Platinum y Sport</h2>
        <div className="overflow-x-auto -mx-6 md:mx-0 mb-8">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#2c4a3b]/5 border-b border-[#2c4a3b]/15">
                <th className="text-left p-3 font-semibold">Presentación</th>
                <th className="text-left p-3 font-semibold">Ideal para</th>
                <th className="text-left p-3 font-semibold">Extras</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#2c4a3b]/10">
                <td className="p-3"><strong>Immunocal Original</strong></td>
                <td className="p-3">Adultos jóvenes, mantenimiento general</td>
                <td className="p-3">Fórmula base</td>
              </tr>
              <tr className="border-b border-[#2c4a3b]/10">
                <td className="p-3"><strong>Immunocal Platinum</strong></td>
                <td className="p-3">Adultos 40+, prevención de pérdida ósea</td>
                <td className="p-3">+ Citrato de K y Mg para pH y huesos</td>
              </tr>
              <tr>
                <td className="p-3"><strong>Immunocal Sport</strong></td>
                <td className="p-3">Atletas, entrenamiento intenso</td>
                <td className="p-3">+ Electrolitos y creatina</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="font-serif text-3xl mt-10 mb-4">Cómo tomar Immunocal correctamente</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">
          La eficacia de Immunocal depende de tomarlo bien:
        </p>
        <ol className="list-decimal pl-6 space-y-2 mb-8 text-[#1a2421]">
          <li>Disolver 1 sobre (10g) en 200ml de agua o jugo <strong>frío</strong>. NUNCA caliente (la temperatura desnaturaliza la proteína).</li>
          <li>Mezclar suavemente (no licuar a alta velocidad — eso también desnaturaliza).</li>
          <li>Tomar con el <strong>estómago vacío</strong>, al menos 20 minutos antes de comer.</li>
          <li>Para mantenimiento: <strong>1 sobre/día</strong>. Para apoyo intensivo (gripe, recuperación): <strong>2-3 sobres/día</strong>.</li>
          <li>Acompañar con buena hidratación y, si es posible, una alimentación rica en azufre natural (ajo, cebolla, brócoli, huevo).</li>
        </ol>

        <h2 className="font-serif text-3xl mt-10 mb-4">¿Dónde comprar Immunocal con descuento en México?</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">
          Immunocal solo se vende a través de la red de Distribuidores Independientes de Immunotec. Comprando por un distribuidor como <strong>Eliora Longevity</strong> obtienes:
        </p>
        <ul className="list-disc pl-6 space-y-2 mb-8 text-[#1a2421]">
          <li><strong>Hasta 45% de descuento</strong> sobre el precio público en el Kit de Inicio Immunotec</li>
          <li>Asesoría personalizada con análisis de tus objetivos de salud</li>
          <li>Un <strong>protocolo wellness gratuito de 3 meses</strong> generado por IA según tu perfil</li>
          <li>Acceso a productos complementarios como XTRIVA, Optimizer Performance, Omega Gen V, Zenity, etc.</li>
        </ul>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          <a href="/#quiz" className="text-[#bc6c25] font-semibold underline underline-offset-4">Empieza con el quiz wellness gratuito →</a>
        </p>

        <h2 className="font-serif text-3xl mt-10 mb-4">Aviso médico</h2>
        <p className="text-sm text-[#5c6b61] leading-relaxed italic">
          Immunocal es un suplemento alimenticio. NO es un medicamento y no diagnostica, trata, cura ni previene enfermedades. Consulta a tu médico antes de iniciar cualquier suplementación, especialmente si estás embarazada, lactando, tomas medicamentos o tienes una condición médica preexistente.
        </p>
      </PillarPageLayout>
    </>
  );
}
